// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { Typography, TypographyProps } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

interface TruncatedTextProps extends Omit<TypographyProps, 'children'> {
  text: string;
}

/**
 * A component that automatically truncates text based on available width.
 * Uses CSS text-overflow for truncation and shows a tooltip with the full
 * text only when truncation occurs.
 */
export function TruncatedText(props: TruncatedTextProps) {
  const { text, ...typographyProps } = props;
  const textRef = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        setIsTruncated(
          textRef.current.scrollWidth > textRef.current.clientWidth
        );
      }
    };

    checkTruncation();

    const resizeObserver = new ResizeObserver(checkTruncation);
    if (textRef.current) {
      resizeObserver.observe(textRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [text]);

  return (
    <ConditionalTooltip
      arrow
      tooltipText={isTruncated ? text : undefined}
      placement="top"
      sx={{ display: 'block', width: '100%' }}
    >
      <Typography
        ref={textRef}
        {...typographyProps}
        sx={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          ...typographyProps.sx,
        }}
      >
        {text}
      </Typography>
    </ConditionalTooltip>
  );
}
